import React, { useRef, useState } from 'react';
import {
  Box, Container, Tab, Tabs,
} from '@mui/material';
import { useSelector } from 'react-redux';
import Page from '../../components/Layout/Page';
import useStyles from './styles';
import CustomHeader from '../../components/General/CustomHeader';
import CustomButton from '../../components/General/CustomButton';
import { ReduxState } from '../../types';
import { UserRole, UserType } from '../../constants/user_types';
import VarietiesSection, { VarietiesSectionHandle } from './components/VarietiesSection';
import SpeciesSection, { SpeciesSectionHandle } from './components/SpeciesSection';
import PhenologicalStateNamesSection, {
  PhenologicalStateNamesSectionHandle,
} from './components/PhenologicalStateNamesSection';

const TAB_VARIETIES = 0;
const TAB_SPECIES = 1;
const TAB_PHEN_STATE_NAMES = 2;

const PAGE_TITLE = 'Especies, variedades y estados fenológicos';

const VarietiesListView = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<number>(TAB_VARIETIES);
  const varietiesRef = useRef<VarietiesSectionHandle | null>(null);
  const speciesRef = useRef<SpeciesSectionHandle | null>(null);
  const phenStateNamesRef = useRef<PhenologicalStateNamesSectionHandle | null>(null);
  const currentUser = useSelector((state: ReduxState) => state.account.user);

  const isAgroAdmin = currentUser?.type === UserType.CDTEC
    && (currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.AGRO);
  const isOperatorOrAbove = currentUser?.type === UserType.CDTEC
    && (currentUser?.role === UserRole.ADMIN
      || currentUser?.role === UserRole.OPERATOR
      || currentUser?.role === UserRole.AGRO);

  const canCreateVariety = isAgroAdmin;
  const canEditVariety = isOperatorOrAbove;
  const canDeleteVariety = isAgroAdmin;
  const canCreateSpecies = isAgroAdmin;
  const canEditSpecies = isAgroAdmin;
  const canDeleteSpecies = isAgroAdmin;
  const canCreatePhenStateName = isAgroAdmin;
  const canEditPhenStateName = isAgroAdmin;
  const canDeletePhenStateName = isAgroAdmin;

  const handleCreate = () => {
    if (activeTab === TAB_VARIETIES) {
      varietiesRef.current?.openCreate();
    } else if (activeTab === TAB_SPECIES) {
      speciesRef.current?.openCreate();
    } else if (activeTab === TAB_PHEN_STATE_NAMES) {
      phenStateNamesRef.current?.openCreate();
    }
  };

  let showCreateButton = false;
  let createButtonText = '';
  if (activeTab === TAB_VARIETIES) {
    showCreateButton = canCreateVariety;
    createButtonText = 'Crear variedad';
  } else if (activeTab === TAB_SPECIES) {
    showCreateButton = canCreateSpecies;
    createButtonText = 'Crear especie';
  } else if (activeTab === TAB_PHEN_STATE_NAMES) {
    showCreateButton = canCreatePhenStateName;
    createButtonText = 'Crear estado fenológico';
  }

  return (
    <Page
      className={classes.root}
      title={PAGE_TITLE}
    >
      <Container maxWidth={false}>
        <CustomHeader
          title={PAGE_TITLE}
          showBackButton={false}
          ActionButton={
            showCreateButton ? (
              <CustomButton
                buttonText={createButtonText}
                color='blue'
                onClick={handleCreate}
              />
            ) : undefined
          }
        />
        <Box mt={2}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            indicatorColor='primary'
            textColor='primary'
          >
            <Tab label='Variedades' />
            <Tab label='Especies' />
            <Tab label='Estados fenológicos' />
          </Tabs>
        </Box>
        {activeTab === TAB_VARIETIES && (
          <VarietiesSection
            ref={varietiesRef}
            canEdit={canEditVariety}
            canDelete={canDeleteVariety}
          />
        )}
        {activeTab === TAB_SPECIES && (
          <SpeciesSection
            ref={speciesRef}
            canEdit={canEditSpecies}
            canDelete={canDeleteSpecies}
          />
        )}
        {activeTab === TAB_PHEN_STATE_NAMES && (
          <PhenologicalStateNamesSection
            ref={phenStateNamesRef}
            canEdit={canEditPhenStateName}
            canDelete={canDeletePhenStateName}
          />
        )}
      </Container>
    </Page>
  );
};

export default VarietiesListView;
